/**
 * Legacy IntersectionObserver: adds .in-view to .animate when scrolled into frame.
 */

class AnimationSystem {
  constructor() {
    this.observer = null;
    this.animatedElements = new Set();
    this.init();
  }

  init() {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () =>
        this.setupObserver(),
      );
    } else {
      this.setupObserver();
    }
  }

  setupObserver() {
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const minRatio = isMobile ? 0.15 : 0.5;
    // Elements taller than ~2x the viewport can never reach a 0.5 ratio,
    // so also trigger once they fill this share of the viewport.
    const minViewportFill = 0.25;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const rootHeight =
            entry.rootBounds?.height || window.innerHeight;
          const fillsViewport =
            entry.intersectionRect.height >= rootHeight * minViewportFill;

          if (entry.intersectionRatio >= minRatio || fillsViewport) {
            this.triggerAnimation(entry.target);
            this.observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: [0, 0.05, 0.1, 0.15, 0.2, 0.3, 0.4, 0.5],
        rootMargin: "0px 0px 10% 0px",
      },
    );

    this.observeAnimationElements();
  }

  observeAnimationElements() {
    document.querySelectorAll(".animate").forEach((element) => {
      if (
        element.closest(".header-full-screen") ||
        element.closest(".projects-hero") ||
        element.hasAttribute("data-no-animate") ||
        this.animatedElements.has(element)
      ) {
        return;
      }
      this.observer.observe(element);
    });
  }

  triggerAnimation(element) {
    this.animatedElements.add(element);
    element.classList.add("in-view");
  }

  observeElement(element) {
    if (this.observer && !this.animatedElements.has(element)) {
      this.observer.observe(element);
    }
  }

  refresh() {
    this.observeAnimationElements();
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    this.animatedElements.clear();
  }
}

export default AnimationSystem;
